import { sanitizeAgentText } from '@wix/ai-scheduling-assistant-common-lib/agentActionLine';
import type { TranscriptResponse } from '../../components/symphony/tasks/agent-conversation-rows';
import type { ActionRow } from './map-events';
import { actorIsWorking, worldActivity, worldActors, worldTaskState, type WorldActivity, type WorldActor, type WorldPlace, type WorldState, type WorldTask } from './living-world';

export const RECAP_DAY_MS = 86_400_000;
export interface RecapTask extends WorldTask {
  createdDate?: string; updatedDate?: string | Date;
  changeLog?: { date?: string | Date; label?: string }[];
}
export interface RecapState { taskId: string; agentId?: string; reviewerId?: string; title: string; state: WorldState }
export interface RecapEvent {
  id: string; at: number; agentId?: string; taskId?: string; title: string; text: string; state: WorldState;
  source: 'action' | 'step' | 'task' | 'snapshot' | 'meeting';
  meetingId?: string;
  reviewerId?: string;
  reviewerActivity?: WorldActivity;
  snapshot?: RecapState[];
  milestone?: RecapChapter['kind'];
}
export interface RecapChapter { index: number; at: number; taskId: string; title: string; kind: 'created' | 'started' | 'review' | 'needsYou' | 'failed' | 'completed' | 'meeting' }
export interface RecapSummary { started: number; completed: number; reviews: number; needsYou: number; failed: number; partial: boolean }
export interface RecapTranscript { task: RecapTask; transcript: TranscriptResponse }

/** A recorded step is a point in time, not evidence that a task ran between two dates. */
export function buildWorldRecap(actions: ActionRow[], tasks: RecapTask[], transcripts: RecapTranscript[], until: number): RecapEvent[] {
  const since = until - RECAP_DAY_MS;
  const events = new Map<string, RecapEvent>();
  const byTask = new Map(tasks.map(task => [task.id, task]));
  const detailedTasks = new Set<string>();
  const add = (event: RecapEvent) => {
    if (Number.isFinite(event.at) && event.at >= since && event.at <= until && event.text) events.set(event.id, event);
  };
  for (const { task, transcript } of transcripts) {
    const rows = [...(transcript.messages ?? []).map(message => ({ message, reviewer: false })),
      ...(transcript.reviewerMessages ?? []).map(message => ({ message, reviewer: true }))];
    for (const [index, { message, reviewer }] of rows.entries()) {
      // Narration belongs in the transcript. The world replays changes in physical activity.
      if (message.role !== 'TOOL_USE' || !message.actionLine) continue;
      const text = sanitizeAgentText(message.actionLine);
      const at = Date.parse(message.createdDate ?? '');
      const activity = worldActivity('', text);
      if (!Number.isFinite(at) || at < since || at > until || activity === 'working' || !text) continue;
      const reviewerId = reviewer ? message.agentId || task.reviewerAgentId : undefined;
      if (reviewer && !reviewerId) continue;
      const agentId = reviewer ? task.agentId : message.agentId || task.agentId;
      const event: RecapEvent = { id: `${reviewer ? 'review-step' : 'step'}:${task.id}:${message.id || index}`, at, agentId,
        taskId: task.id, title: task.title || text, text, state: reviewer ? 'review' : activity, source: 'step',
        ...(reviewer ? { reviewerId, reviewerActivity: activity } : {}) };
      add(event); detailedTasks.add(task.id);
    }
  }
  for (const action of actions) {
    if (action.relatedTaskId && detailedTasks.has(action.relatedTaskId)) continue;
    const text = sanitizeAgentText(action.action || action.label);
    const task = action.relatedTaskId ? byTask.get(action.relatedTaskId) : undefined;
    const activity = worldActivity(action.label, text);
    if (activity === 'working') continue;
    add({ id: `action:${action.id}`, at: Date.parse(action.createdDate ?? ''), agentId: action.agentId || task?.agentId,
      taskId: action.relatedTaskId, title: task?.title || text, text, state: activity, source: 'action' });
  }
  const previous = new Map<string, RecapEvent>();
  const activity = [...events.values()].sort((a, b) => a.at - b.at || a.id.localeCompare(b.id)).filter(event => {
    const key = `${event.taskId || event.id}:${event.agentId || ''}:${event.reviewerId || ''}`;
    const last = previous.get(key);
    previous.set(key, event);
    // Reading ten files in a row is one scene, even when their action lines differ.
    return !last || last.state !== event.state || last.reviewerActivity !== event.reviewerActivity || event.at - last.at > 5 * 60_000;
  });
  return completeWorldRecap(activity, tasks, until);
}

const time = (value?: string | Date) => new Date(value || '').getTime();
const CHANGE_STATES: Record<string, WorldState> = {
  started: 'unknown', reopened: 'unknown', 'review:changes-requested': 'unknown',
  submission: 'review', 'review:approved': 'done', completed: 'done', cancelled: 'done', superseded: 'done',
  'review:failed': 'failed', failed: 'failed', 'closed:user-directed': 'failed',
};

function persistedState(task: RecapTask, at: number): WorldState {
  // Runtime heartbeats, tool props and joined decisions describe NOW, not the last task update.
  const state = worldTaskState({ ...task, runInFlight: undefined, currentAction: undefined,
    decisionPrompts: undefined, decisionPromptsUnavailable: undefined, reviewStage: undefined }, undefined, at);
  return state === 'unknown' && lastHistoricalChange(task, at)?.label === 'submission' ? 'review' : state;
}

function lastHistoricalChange(task: RecapTask, at: number) {
  return (task.changeLog || []).filter(change => time(change.date) <= at && CHANGE_STATES[change.label || ''])
    .sort((a, b) => time(a.date) - time(b.date)).pop();
}

function historicalState(task: RecapTask, at: number): WorldState {
  const updated = time(task.updatedDate);
  if (Number.isFinite(updated) && updated <= at) return persistedState(task, at);
  const last = lastHistoricalChange(task, at);
  return last ? CHANGE_STATES[last.label!] : 'unknown';
}

/** A current plan may name past work, but only dated evidence can give it a past status. */
export function recapTaskStatus(task: RecapTask, at: number): string | undefined {
  if (time(task.updatedDate) <= at) return task.status;
  const status: Record<string, string> = { started: 'IN_PROGRESS', reopened: 'IN_PROGRESS', 'review:changes-requested': 'IN_PROGRESS',
    submission: 'IN_REVIEW', 'review:approved': 'COMPLETED', completed: 'COMPLETED', cancelled: 'CANCELLED', superseded: 'MOOT',
    'review:failed': 'FAILED', failed: 'FAILED', 'closed:user-directed': 'FAILED' };
  return status[lastHistoricalChange(task, at)?.label || ''];
}

/** Seed the whole world in one frame, then apply recorded changes, ending at the frozen live snapshot. */
export function completeWorldRecap(activity: RecapEvent[], tasks: RecapTask[], until: number): RecapEvent[] {
  const since = until - RECAP_DAY_MS;
  const existed = (task: RecapTask, at: number) => [task.createdDate, task.updatedDate, ...(task.changeLog || []).map(change => change.date)]
    .some(date => time(date) <= at);
  const snapshot = (at: number, live: boolean): RecapEvent => ({
    id: live ? 'recap:end' : 'recap:start', at, title: '', text: '', state: 'unknown', source: 'snapshot',
    snapshot: tasks.filter(task => live ? !(time(task.createdDate) > at) : existed(task, at)).map(task => ({
      taskId: task.id, agentId: task.agentId, title: task.title || '',
      reviewerId: live || time(task.updatedDate) <= at ? task.reviewerAgentId : undefined,
      state: live ? worldTaskState({ ...task, reviewStage: task.reviewStage
        ?? (lastHistoricalChange(task, at)?.label === 'submission' ? 'submitted' : undefined) }, undefined, at) : historicalState(task, at),
    })).filter(task => task.state !== 'done'),
  });
  const events = activity.filter(event => event.source === 'step' || event.source === 'action');
  const add = (task: RecapTask, id: string, at: number, state: WorldState, reviewerId?: string, milestone?: RecapChapter['kind']) => {
    if (Number.isFinite(at) && at >= since && at <= until) events.push({ id, at, taskId: task.id, agentId: task.agentId,
      title: task.title || '', text: '', state, source: 'task', reviewerId, milestone });
  };
  for (const task of tasks) {
    if (time(task.createdDate) > since) add(task, `created:${task.id}`, time(task.createdDate), 'unknown', undefined, 'created');
    for (const [i, change] of (task.changeLog || []).entries()) {
      const state = CHANGE_STATES[change.label || ''];
      const milestone = change.label === 'started' ? 'started' : change.label === 'submission' ? 'review'
        : ['completed', 'review:approved'].includes(change.label || '') ? 'completed' : state === 'failed' ? 'failed' : undefined;
      if (state) add(task, `change:${task.id}:${i}`, time(change.date), state, state === 'review' ? task.reviewerAgentId : undefined, milestone);
    }
    if (Number.isFinite(time(task.updatedDate))) {
      const state = persistedState(task, time(task.updatedDate));
      const milestone = task.status === 'COMPLETED' ? 'completed' : state === 'needsYou' ? 'needsYou' : state === 'review' ? 'review' : state === 'failed' ? 'failed' : undefined;
      add(task, `task:${task.id}`, time(task.updatedDate), state, task.reviewerAgentId, milestone);
    }
  }
  // Status changes win ties with tools. Snapshots are boundaries, never narrated activities.
  events.sort((a, b) => a.at - b.at || Number(a.source === 'task') - Number(b.source === 'task') || a.id.localeCompare(b.id));
  return [snapshot(since, false), ...events.filter(event => event.at >= since && event.at <= until), snapshot(until, true)];
}

export function recapHasUnknownStart(events: RecapEvent[]): boolean {
  return !!events[0]?.snapshot?.some(task => task.state === 'unknown');
}

export function recapArrivalIds(event?: RecapEvent): string[] {
  return event?.taskId && event.source === 'task' && event.id === `created:${event.taskId}` ? [event.taskId] : [];
}

export function recapChapters(events: RecapEvent[]): RecapChapter[] {
  const seen = new Set<string>();
  return events.flatMap((event, index) => {
    if (event.source === 'meeting' && event.milestone === 'meeting') return [{ index, at: event.at, taskId: '', title: event.title, kind: 'meeting' as const }];
    if (!event.taskId || !event.milestone || event.source === 'snapshot') return [];
    const key = `${event.taskId}:${event.milestone}`;
    if (seen.has(key)) return [];
    seen.add(key);
    return [{ index, at: event.at, taskId: event.taskId, title: event.title, kind: event.milestone }];
  });
}

export function recapSummary(events: RecapEvent[], partial = false): RecapSummary {
  const chapters = recapChapters(events);
  const count = (kind: RecapChapter['kind']) => chapters.filter(chapter => chapter.kind === kind).length;
  return { started: count('started'), completed: count('completed'), reviews: count('review'), needsYou: count('needsYou'), failed: count('failed'),
    partial: partial || recapHasUnknownStart(events) };
}

export function recapTaskId(events: RecapEvent[], actorId: string): string | undefined {
  return events.some(event => event.taskId === actorId || event.snapshot?.some(task => task.taskId === actorId)) ? actorId : undefined;
}

export function recapActors(events: RecapEvent[], index: number, places: WorldPlace[]): WorldActor[] {
  const current = events[index];
  if (!current) return [];
  const recent = new Map<string, RecapEvent>();
  for (const event of events.slice(0, index + 1)) {
    if (event.source === 'meeting') continue;
    if (event.source === 'snapshot') {
      recent.clear();
      for (const task of event.snapshot || []) recent.set(task.taskId, { ...event, ...task, id: task.taskId });
    } else if (event.taskId || current.at - event.at <= 5 * 60_000) recent.set(event.taskId || event.id, event);
  }
  const visible = [...recent.values()].filter(event => event.state !== 'done' || event === current).map(event => ({ ...event,
    // A tool step proves activity at that instant, not continuous work during quiet hours.
    state: actorIsWorking(event.state) && current.at - event.at > 5 * 60_000 ? 'unknown' as const : event.state,
  }));
  const positioned = worldActors(visible.map(event => ({ id: event.taskId || event.id, agentId: event.agentId,
    title: event.title, currentAction: event.text, reviewerAgentId: event.reviewerId,
    status: event.state === 'review' ? 'IN_REVIEW' : 'IN_PROGRESS', runInFlight: true })), places, {}, current.at);
  return positioned.map((actor, i) => ({ ...actor, state: visible[i].state,
    reviewerActivity: current.at - visible[i].at <= 5 * 60_000 ? visible[i].reviewerActivity : undefined }));
}
